<template>
  <b-modal
    :id="modalId"
    body-class="p-0"
    size="md"
    no-close-on-backdrop
    static
    lazy
    @hidden="searchQuery = ''"
  >
    <template #modal-title>
      <div>
        <span>{{ displayTitle }}</span>
        <p class="font-size-base text-muted font-weight-normal mb-0 mt-1">
          {{ descriptionText }}
        </p>
      </div>
    </template>
    <template #modal-header-close>
      <i class="fa fa-close" />
    </template>

    <div
      v-if="permissions.length === 0"
      class="p-4 text-center text-muted"
    >
      {{ emptyStateText }}
    </div>

    <template v-else>
      <div class="px-3 pt-0 pb-3 border-bottom bg-light">
        <b-form-input
          v-model="searchQuery"
          size="sm"
          placeholder="Filter by name or action"
          class="border-0 bg-white"
        />
      </div>
      <b-list-group class="permission-list">
        <b-list-group-item
          v-for="perm in filteredPermissions"
          :key="perm.name"
          action
          class="border-0 border-bottom rounded-0 px-4 py-2"
        >
          <div class="permission-name text-dark">
            {{ perm.name | humanizedFormat | capitalizeFormat }}
          </div>
          <div class="permission-rules text-muted small mt-1">
            <span
              v-for="(rule, idx) in perm.rulesList"
              :key="idx"
            >
              {{ rule | humanizedFormat | capitalizeFormat }}<template v-if="idx < perm.rulesList.length - 1">, </template>
            </span>
          </div>
        </b-list-group-item>
      </b-list-group>
      <div
        v-if="filteredPermissions.length === 0 && searchQuery"
        class="p-4 text-center text-muted small"
      >
        No permissions match "{{ searchQuery }}".
      </div>
    </template>

    <template #modal-footer>
      <b-button
        variant="primary"
        @click="closeModal"
      >
        Close
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { map as _map, union as _union } from 'lodash';

export default {
  name: 'RolePermissionsModal',
  props: {
    modalId: {
      type: String,
    },
    user: {
      type: Object,
      default: null,
    },
    role: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchQuery: '',
    };
  },
  methods: {
    closeModal() {
      this.$bvModal.hide(this.modalId);
    },
  },
  computed: {
    isUserMode() {
      return this.user != null && (this.user.name != null || this.user.id != null);
    },
    displayTitle() {
      if (this.isUserMode && this.user?.name) {
        const name = this.$options.filters.capitalizeFormat(this.$options.filters.humanizedFormat(this.user.name));
        return `${name} Permissions`;
      }
      if (this.role?.name) {
        return this.$options.filters.capitalizeFormat(this.$options.filters.humanizedFormat(this.role.name));
      }
      return 'Permissions';
    },
    descriptionText() {
      return this.isUserMode
        ? 'What this user can do in the app. Each item shows which actions are allowed.'
        : 'What this role can do in the app. Each item shows which actions are allowed.';
    },
    emptyStateText() {
      return this.isUserMode
        ? 'No permissions for this user.'
        : 'No permissions for this role.';
    },
    permissions() {
      if (this.isUserMode && this.user?.roles?.length) {
        const merged = {};
        this.user.roles.forEach((r) => {
          _map(r.permissions || {}, (rules, name) => {
            const list = Array.isArray(rules) ? rules : [rules];
            merged[name] = merged[name] ? _union(merged[name], list) : list;
          });
        });
        return _map(merged, (rulesList, name) => ({ name, rulesList }));
      }
      if (this.role?.permissions) {
        return _map(this.role.permissions, (rules, name) => ({
          name,
          rulesList: Array.isArray(rules) ? rules : [rules],
        }));
      }
      return [];
    },
    filteredPermissions() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) {
        return this.permissions;
      }
      return this.permissions.filter((p) => {
        const nameMatch = p.name.toLowerCase().includes(q);
        const rulesMatch = p.rulesList.some((r) => String(r).toLowerCase().includes(q));
        return nameMatch || rulesMatch;
      });
    },
  },
};
</script>

<style scoped>
.permission-list {
  max-height: 50vh;
  overflow-y: auto;
}
.permission-name {
  font-weight: 500;
}
</style>
